import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Mail, X, Send } from "lucide-react";
import { toast } from "sonner";

export default function EmailInvoiceModal({ invoice, customer, onClose, onSent }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    recipient_email: customer?.email || "",
    cc: "",
    subject: invoice ? `Invoice ${invoice.invoice_number}` : "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  useEffect(() => {
    api.get("/email/config").then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
    if (invoice?.id) {
      api.get(`/invoices/${invoice.id}/email-log`).then((r) => setLog(r.data.logs || [])).catch(() => {});
    }
  }, [invoice?.id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.recipient_email) return toast.error("Recipient email is required");
    setBusy(true);
    try {
      const payload = {
        recipient_email: form.recipient_email,
        subject: form.subject,
        message: form.message,
        cc: form.cc.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post(`/invoices/${invoice.id}/email`, payload);
      toast.success(`Email sent to ${data.to}`);
      onSent?.(data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Unknown error";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" data-testid="email-invoice-modal">
      <div className="bg-white border-t-2 sm:border-2 border-[#1F1E1B] w-full sm:max-w-lg max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between border-b border-[#1F1E1B] px-4 sm:px-5 py-3 bg-[#F5F4F0] sticky top-0 z-10">
          <h2 className="font-display text-lg font-bold flex items-center gap-2"><Mail className="w-4 h-4" /> Email Invoice {invoice?.invoice_number}</h2>
          <button onClick={onClose} data-testid="email-modal-close" className="text-[#66645E] hover:text-[#1F1E1B] text-2xl leading-none px-2"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {config && !config.configured && (
            <div className="bg-[#FFF6E5] border-2 border-[#C77E1A] p-3 text-xs text-[#5C4A2B]" data-testid="email-not-configured">
              <strong>Email isn't configured yet.</strong>
              <div className="mt-1">Add <code>RESEND_API_KEY</code> to backend/.env (sign up free at resend.com), then restart the backend.</div>
            </div>
          )}

          {config && config.configured && (
            <div className="text-[11px] text-[#66645E] border border-[#E5E2D6] p-2 bg-[#FAF8F2]" data-testid="email-from-line">
              Sending from <strong>{config.from}</strong>
              {config.reply_to && <> · replies go to <strong>{config.reply_to}</strong></>}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-3" data-testid="email-invoice-form">
            <Field label="To" required>
              <input data-testid="email-to-input" type="email" required value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} placeholder="customer@example.com" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <Field label="CC (comma-separated, optional)">
              <input data-testid="email-cc-input" value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <Field label="Subject" required>
              <input data-testid="email-subject-input" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <Field label="Personal note (optional)">
              <textarea data-testid="email-message-input" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Hi — invoice for last week's work is attached. Thanks!" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} data-testid="email-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" disabled={busy || !config?.configured} data-testid="email-send-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send Email"}
              </button>
            </div>
          </form>

          {log.length > 0 && (
            <div className="border-t border-[#E5E2D6] pt-3" data-testid="email-log-list">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#66645E] mb-2">Previously sent</div>
              <ul className="space-y-1 text-xs">
                {log.slice(0, 5).map((l) => (
                  <li key={l.id} className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 ${l.status === "sent" ? "bg-[#1A4135]" : "bg-[#BC4727]"}`} />
                    <span className="tabular text-[#66645E]">{(l.sent_at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="truncate">{l.recipient}</span>
                    {l.status === "error" && <span className="text-[#BC4727] text-[10px]">{l.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1.5">
        {label}{required && " *"}
      </div>
      {children}
    </label>
  );
}
